/**
 * `ResourceRef` is the (resource, version) tuple every record-service route is
 * keyed on. The record-service backend is fully dynamic — `/record-service/v1/
 * {resource}/{resourceVersion}/...` works for any registered schema — so every
 * dynamic-entity feature (Material, Release, ReleaseBundle, …) really only
 * needs to forward this pair to the generic CRUD helpers.
 *
 * Use the well-known registry below for first-party schemas. Feature code that
 * defines its own resource (e.g. a per-tenant custom entity) can construct an
 * inline ref with the same shape.
 */
export interface ResourceRef {
  readonly resource: string;
  readonly version: string;
}

/**
 * Construct a `ResourceRef` literal. Convenience over an object literal so
 * call sites read as `recordRef("Material", "1.0.0")` and the inference picks
 * up the narrow `readonly` shape.
 */
export const recordRef = (resource: string, version: string): ResourceRef => ({
  resource,
  version,
});

/**
 * Stable string identity for a ref, used in cache keys and Maps.
 * Format: `"<resource>@<version>"`.
 */
export const formatRef = ({ resource, version }: ResourceRef): string => `${resource}@${version}`;

/** Structural equality for two refs. */
export const refsEqual = (a: ResourceRef | undefined, b: ResourceRef | undefined): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.resource === b.resource && a.version === b.version;
};

/**
 * Well-known refs for first-party schemas bootstrapped into the
 * record-service on startup (see `centric-service/src/main/resources/
 * system-schemas/*.json` and the `flavours/saas-plm-base/**` overlay).
 *
 * Tenants may register additional schemas at runtime; consumers of those
 * should construct ad-hoc refs via `recordRef("…", "…")` rather than
 * extending this object.
 */
export const recordRefs = {
  Material: recordRef("Material", "1.0.0"),
  MaterialAttributes: recordRef("MaterialAttributes", "1.0.0"),
  MaterialSourcing: recordRef("MaterialSourcing", "1.0.0"),
  MaterialComposition: recordRef("MaterialComposition", "1.0.0"),
  Composition: recordRef("Composition", "1.0.0"),
  SkuMaterial: recordRef("SkuMaterial", "1.0.0"),

  Release: recordRef("Release", "1.0.0"),
  ReleaseBundle: recordRef("ReleaseBundle", "1.0.0"),
  ReleaseItem: recordRef("ReleaseItem", "1.0.0"),
  SchemaPromotionChange: recordRef("SchemaPromotionChange", "1.0.0"),
} as const satisfies Record<string, ResourceRef>;

export type WellKnownRecordKey = keyof typeof recordRefs;
